import React, { useEffect, useId, useState } from 'react';
import { X, AlertTriangle, CheckCircle2, ExternalLink, Loader2 } from 'lucide-react';
import './Modal.css';

const MAX_REASON = 500;

/**
 * Two-step cancel dialog.
 * Step 1: optional reason, then onConfirm(reason) saves the cancellation in the app and returns the
 * response data ({success, thrivecart_cancelled}) or null on failure.
 * Step 2: when the backend also stopped billing in ThriveCart (thrivecart_cancelled) the dialog just
 * confirms; otherwise it sends the customer to ThriveCart to finish.
 * onClose(done) is called with true when step 2 was reached, so the page can refresh.
 */
const CancelSubscriptionDialog = ({ isOpen, onClose, onConfirm, periodEnd, thrivecartUrl }) => {
  const titleId = useId();
  const reasonId = useId();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [billingStopped, setBillingStopped] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setBusy(false);
      setDone(false);
      setBillingStopped(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const close = () => {
    if (!busy) onClose(done);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const result = await onConfirm(reason.trim());
      if (result && result.success) {
        setBillingStopped(!!result.thrivecart_cancelled);
        setDone(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const accessLine = periodEnd ? `until ${periodEnd}` : 'until the end of your billing period';

  return (
    <div className="ui-dialog-overlay" onClick={close}>
      <div
        className="ui-dialog ui-dialog--confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ui-dialog-head">
          <div className="ui-dialog-heading">
            <span
              className={`ui-dialog-icon ${done ? 'ui-dialog-icon--success' : 'ui-dialog-icon--danger'}`}
              aria-hidden="true"
            >
              {done ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
            </span>
            <h2 id={titleId} className="ui-dialog-title">
              {done ? (billingStopped ? 'Subscription cancelled' : 'One last step: stop billing in ThriveCart') : 'Cancel your subscription?'}
            </h2>
          </div>
          <button type="button" onClick={close} className="ui-dialog-close" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {!done ? (
          <>
            <div className="ui-dialog-body ui-dialog-body--indented">
              <p className="m-0 mb-3 text-sm text-[#5f5c72]">
                You keep access {accessLine}. Your plan will not renew after that.
              </p>
              <label htmlFor={reasonId} className="block text-sm font-semibold text-[#16141f] mb-1">
                Why are you cancelling? <span className="font-normal text-[#9794aa]">(optional)</span>
              </label>
              <textarea
                id={reasonId}
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON))}
                rows={3}
                disabled={busy}
                placeholder="Anything that would help us improve"
                className="w-full text-sm border border-[#e9e8f1] rounded-lg p-2 resize-y"
              />
            </div>
            <div className="ui-dialog-foot">
              <button type="button" onClick={close} disabled={busy} className="btn btn-secondary">
                Keep subscription
              </button>
              <button type="button" onClick={confirm} disabled={busy} className="btn btn-danger">
                {busy ? <Loader2 size={16} className="animate-spin" /> : 'Cancel subscription'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="ui-dialog-body ui-dialog-body--indented">
              <p className="m-0 mb-3 text-sm text-[#5f5c72]">
                Your cancellation is saved and you keep access {accessLine}.
              </p>
              {billingStopped ? (
                <p className="m-0 text-sm text-[#16141f] font-semibold">
                  Your billing has been stopped, so you will not be charged again.
                </p>
              ) : (
                <p className="m-0 text-sm text-[#16141f] font-semibold">
                  We could not stop billing automatically. Payments are handled by ThriveCart, so you
                  also need to cancel there or you will be billed again.
                </p>
              )}
            </div>
            <div className="ui-dialog-foot">
              {billingStopped ? (
                <button type="button" onClick={close} className="btn btn-primary">
                  Done
                </button>
              ) : (
                <>
                  <button type="button" onClick={close} className="btn btn-secondary">
                    I'll do it later
                  </button>
                  <a
                    href={thrivecartUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary inline-flex items-center gap-1.5"
                  >
                    Open ThriveCart <ExternalLink size={14} />
                  </a>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CancelSubscriptionDialog;
